// localStorage-based storage implementation (replacing Supabase)
import { Favorite, PriceTracking, PriceHistory, Comparison, SearchHistoryItem, ProductListing } from '../types';

// Storage keys
const STORAGE_KEYS = {
  FAVORITES: 'favorites',
  PRICE_TRACKING: 'price_tracking',
  PRICE_HISTORY: 'price_history',
  COMPARISONS: 'comparisons',
  SEARCH_HISTORY: 'search_history',
} as const;

// Helper functions for localStorage operations
function getStorageItem<T>(key: string): T[] {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : [];
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return [];
  }
}

function setStorageItem<T>(key: string, value: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error writing ${key} to localStorage:`, error);
    throw error;
  }
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Favorites functions
export async function addFavorite(product: ProductListing) {
  const favorites = getStorageItem<Favorite>(STORAGE_KEYS.FAVORITES);
  
  // Check if already exists
  const existing = favorites.find((fav) => fav.product_id === product.id);
  if (existing) {
    return existing;
  }

  const newFavorite = {
    id: generateId(),
    user_id: 'local', // No user authentication needed for localStorage
    product_id: product.id,
    title: product.title,
    price: product.price,
    image_url: product.images?.[0],
    marketplace: product.marketplace,
    url: product.url,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  favorites.push(newFavorite);
  setStorageItem(STORAGE_KEYS.FAVORITES, favorites);
  return newFavorite;
}

export async function removeFavorite(productId: string) {
  const favorites = getStorageItem<Favorite>(STORAGE_KEYS.FAVORITES);
  const filtered = favorites.filter((fav) => fav.product_id !== productId);
  setStorageItem(STORAGE_KEYS.FAVORITES, filtered);
}

export async function getFavorites() {
  const favorites = getStorageItem<Favorite>(STORAGE_KEYS.FAVORITES);
  // Sort by created_at descending
  return favorites.sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

export async function isFavorite(productId: string): Promise<boolean> {
  const favorites = getStorageItem<Favorite>(STORAGE_KEYS.FAVORITES);
  return favorites.some((fav) => fav.product_id === productId);
}

// Price tracking functions
export async function startPriceTracking(product: ProductListing, targetPrice?: number) {
  const tracking = getStorageItem<PriceTracking>(STORAGE_KEYS.PRICE_TRACKING);
  
  // Check if already exists
  const existing = tracking.find((t) => t.product_id === product.id);
  const now = new Date().toISOString();
  
  if (existing) {
    // Update existing
    existing.current_price = product.price;
    const currentPrice = product.price ?? 0;
    const existingLowest = existing.lowest_price ?? currentPrice;
    const existingHighest = existing.highest_price ?? currentPrice;
    existing.lowest_price = Math.min(existingLowest, currentPrice);
    existing.highest_price = Math.max(existingHighest, currentPrice);
    if (targetPrice !== undefined) {
      existing.target_price = targetPrice;
    }
    existing.last_checked_at = now;
    existing.updated_at = now;
    setStorageItem(STORAGE_KEYS.PRICE_TRACKING, tracking);
    return existing;
  }

  const newTracking = {
    id: generateId(),
    user_id: 'local',
    product_id: product.id,
    title: product.title,
    current_price: product.price,
    lowest_price: product.price,
    highest_price: product.price,
    target_price: targetPrice ?? product.price,
    price_alert_enabled: true,
    created_at: now,
    updated_at: now,
    last_checked_at: now,
  };

  tracking.push(newTracking);
  setStorageItem(STORAGE_KEYS.PRICE_TRACKING, tracking);
  return newTracking;
}

export async function getPriceTracking() {
  const tracking = getStorageItem<PriceTracking>(STORAGE_KEYS.PRICE_TRACKING);
  // Filter by price_alert_enabled and return
  return tracking.filter((t) => t.price_alert_enabled === true);
}

export async function removePriceTracking(trackingId: string) {
  const tracking = getStorageItem<PriceTracking>(STORAGE_KEYS.PRICE_TRACKING);
  const filtered = tracking.filter((t) => t.id !== trackingId);
  setStorageItem(STORAGE_KEYS.PRICE_TRACKING, filtered);
  
  // Also remove associated price history
  const history = getStorageItem<PriceHistory>(STORAGE_KEYS.PRICE_HISTORY);
  const filteredHistory = history.filter((h) => h.tracking_id !== trackingId);
  setStorageItem(STORAGE_KEYS.PRICE_HISTORY, filteredHistory);
}

export async function addPriceHistory(trackingId: string, price: number) {
  const history = getStorageItem<PriceHistory>(STORAGE_KEYS.PRICE_HISTORY);
  
  const newHistoryItem = {
    id: generateId(),
    tracking_id: trackingId,
    price,
    recorded_at: new Date().toISOString(),
  };

  history.push(newHistoryItem);
  setStorageItem(STORAGE_KEYS.PRICE_HISTORY, history);
}

export async function getPriceHistory(trackingId: string) {
  const history = getStorageItem<PriceHistory>(STORAGE_KEYS.PRICE_HISTORY);
  const filtered = history.filter((h) => h.tracking_id === trackingId);
  // Sort by recorded_at ascending
  return filtered.sort((a, b) => 
    new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
  );
}

// Comparison functions
export async function createComparison(name: string, productIds: string[]) {
  const comparisons = getStorageItem<Comparison>(STORAGE_KEYS.COMPARISONS);
  
  const newComparison = {
    id: generateId(),
    user_id: 'local',
    name,
    product_ids: productIds,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  comparisons.push(newComparison);
  setStorageItem(STORAGE_KEYS.COMPARISONS, comparisons);
  return newComparison;
}

export async function getComparisons() {
  const comparisons = getStorageItem<Comparison>(STORAGE_KEYS.COMPARISONS);
  // Sort by created_at descending
  return comparisons.sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

export async function removeComparison(comparisonId: string) {
  const comparisons = getStorageItem<Comparison>(STORAGE_KEYS.COMPARISONS);
  const filtered = comparisons.filter((c) => c.id !== comparisonId);
  setStorageItem(STORAGE_KEYS.COMPARISONS, filtered);
}

// Search history functions
export async function addSearchHistory(query: string, location: string | null = null) {
  const history = getStorageItem<SearchHistoryItem>(STORAGE_KEYS.SEARCH_HISTORY);
  
  const newHistoryItem = {
    id: generateId(),
    user_id: 'local',
    query,
    location,
    created_at: new Date().toISOString(),
  };

  history.push(newHistoryItem);
  // Keep only last 10 items
  const limited = history.slice(-10);
  setStorageItem(STORAGE_KEYS.SEARCH_HISTORY, limited);
}

export async function getSearchHistory() {
  const history = getStorageItem<SearchHistoryItem>(STORAGE_KEYS.SEARCH_HISTORY);
  // Sort by created_at descending and limit to 10
  return history
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 10);
}

// Legacy export for backward compatibility (not used but exported in original)
export const storage = {
  auth: {
    getUser: async () => ({ data: { user: null }, error: null }),
  },
  from: () => ({
    insert: () => ({ data: null, error: null }),
    select: () => ({
      eq: () => ({
        order: () => ({ data: [], error: null }),
        maybeSingle: () => ({ data: null, error: null }),
      }),
    }),
    delete: () => ({
      eq: () => ({ error: null }),
    }),
    upsert: () => ({ data: null, error: null }),
  }),
};
